import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material'

import { SearchInput } from './search-input.component'
import { IFilter, ISortSelectOption } from '../filter/types/filter.types'

import { useStyles } from './styles'
import { HelperWrapper } from '../helper-wrapper'
import { RefreshIcon } from 'src/assets/icons/v2/RefreshIcon'

interface IProps<T> {
  selectName?: string
  items: ISortSelectOption[]
  handleChange: (e: SelectChangeEvent<T>) => void
  value: T
  inputName?: string
  errorText?: string | ReactNode
  multiple?: boolean
  disabled?: boolean
  renderValue?: (selected: T) => string
  withSearch?: boolean
  setFilter?: Dispatch<SetStateAction<IFilter>>
  filter?: IFilter
  paramName?: string
  noClear?: boolean
  code?: string | null
}

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1

function FormSelect<T>(props: IProps<T>) {
  const classes = useStyles()
  const theme = useTheme()

  const [searchText, setSearchText] = useState('')

  const displayedItems = useMemo(
    () =>
      props.items.filter(
        (item) =>
          (item.label && containsText(item.label, searchText)) ||
          (item.code && containsText(item.code, searchText)),
      ),
    [searchText, props.items],
  )

  const handleClearFilter = () => {
    if (props.withSearch) {
      setSearchText('')
    }

    props.setFilter!({ ...props.filter, [props.paramName as string]: ['all'] })
  }

  return (
    <FormControl className={classes.selectionOptionsFormControl}>
      <Select
        name={props.inputName}
        className={classes.formSelect}
        value={props.value}
        onChange={props.handleChange}
        multiple={props.multiple}
        renderValue={
          props.renderValue ||
          (Array.isArray(props.value)
            ? (selected) =>
                Array.isArray(selected) &&
                (selected.length > 1
                  ? props.items.find((item) => item.value === selected[0])?.label + '...'
                  : props.items.find((item) => item.value === selected[0])?.label)
            : undefined)
        }
        disabled={props.disabled}
        startAdornment={
          <InputAdornment position="start">
            <Typography className={classes.selectLabel}>
              {props.selectName && props.selectName + ':'}
            </Typography>
          </InputAdornment>
        }
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            className: classes.selectMenu,
          },
        }}
      >
        {props.withSearch && <SearchInput searchText={searchText} setSearch={setSearchText} />}
        {displayedItems.map(({ label, value: itemValue, code }) => {
          if (label !== 'all')
            return (
              <MenuItem
                disabled={props.disabled}
                key={itemValue}
                className={classes.selectItem}
                value={itemValue}
                disableGutters={props.multiple}
              >
                {props.multiple && (
                  <Checkbox
                    size="small"
                    checked={Array.isArray(props.value) && props.value.indexOf(itemValue) > -1}
                  />
                )}
                <div>
                  <div>{label}</div>
                  {code && <div className={classes.codeStyle}>#{code}</div>}
                </div>
              </MenuItem>
            )
          return
        })}
      </Select>
      <FormHelperText sx={{ color: theme.palette.custom.warning2 }}>
        {props.errorText}
      </FormHelperText>
      {!props.noClear && (
        <Button onClick={handleClearFilter} className={classes.clearButton} color={'inherit'}>
          <p className={classes.clearButtonText}>{<HelperWrapper t="clear-filter-option" />}</p>
          <RefreshIcon size={14} />
        </Button>
      )}
    </FormControl>
  )
}

export { FormSelect }
